import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import type { LogisticOperatorsFilter } from '../models';
import { splitExclusiveFilterFromParent } from '../utils/logisticOperatorsSearchBar';

interface UseLogisticOperatorsSearchBarLocalStateParams {
  filter: LogisticOperatorsFilter;
  search: (next: LogisticOperatorsFilter) => void;
  initialFilter: LogisticOperatorsFilter;
}

interface UseLogisticOperatorsSearchBarLocalStateResult {
  localFilter: LogisticOperatorsFilter;
  setLocalFilter: Dispatch<SetStateAction<LogisticOperatorsFilter>>;
  nameInput: string;
  businessInput: string;
  setNameInput: (next: string) => void;
  setBusinessInput: (next: string) => void;
  setNameInputState: Dispatch<SetStateAction<string>>;
  setBusinessInputState: Dispatch<SetStateAction<string>>;
  nameId: string;
  businessId: string;
  resetFilters: () => void;
  isNameFieldDisabled: boolean;
  isBusinessFieldDisabled: boolean;
}

const useLogisticOperatorsSearchBarLocalState = ({
  filter,
  search,
  initialFilter,
}: UseLogisticOperatorsSearchBarLocalStateParams): UseLogisticOperatorsSearchBarLocalStateResult => {
  const [localFilter, setLocalFilter] =
    useState<LogisticOperatorsFilter>(filter);
  const [nameInput, setNameInputState] = useState('');
  const [businessInput, setBusinessInputState] = useState('');

  useEffect(() => {
    const { normalized, hadConflict } = splitExclusiveFilterFromParent(filter);
    if (hadConflict) {
      setLocalFilter(normalized);
      setNameInputState(filter.name ?? '');
      setBusinessInputState('');
      search(normalized);
      return;
    }
    setLocalFilter(filter);
    setNameInputState(filter.name ?? '');
    setBusinessInputState(filter.businessName ?? '');
  }, [filter, search]);

  const nameId = localFilter.selectedOperatorIdFromName?.trim() ?? '';
  const businessId = localFilter.selectedOperatorIdFromBusiness?.trim() ?? '';

  const isNameChannelActive =
    nameInput.trim().length > 0 || nameId.length > 0;
  const isBusinessChannelActive =
    businessInput.trim().length > 0 || businessId.length > 0;

  const setNameInput = useCallback((next: string) => {
    if (next.trim().length > 0) {
      setLocalFilter((previous) => {
        const businessIdTrim =
          previous.selectedOperatorIdFromBusiness?.trim() ?? '';
        if (businessIdTrim.length === 0) return previous;
        return {
          ...previous,
          selectedOperatorIdFromBusiness: undefined,
        };
      });
      setBusinessInputState((previous) =>
        previous.length > 0 ? '' : previous
      );
    }
    setNameInputState(next);
  }, []);

  const setBusinessInput = useCallback((next: string) => {
    if (next.trim().length > 0) {
      setLocalFilter((previous) => {
        const nameIdTrim = previous.selectedOperatorIdFromName?.trim() ?? '';
        if (nameIdTrim.length === 0) return previous;
        return {
          ...previous,
          selectedOperatorIdFromName: undefined,
        };
      });
      setNameInputState((previous) =>
        previous.length > 0 ? '' : previous
      );
    }
    setBusinessInputState(next);
  }, []);

  const resetFilters = useCallback(() => {
    setLocalFilter(initialFilter);
    setNameInputState('');
    setBusinessInputState('');
    search(initialFilter);
  }, [initialFilter, search]);

  return {
    localFilter,
    setLocalFilter,
    nameInput,
    businessInput,
    setNameInput,
    setBusinessInput,
    setNameInputState,
    setBusinessInputState,
    nameId,
    businessId,
    resetFilters,
    isNameFieldDisabled: isBusinessChannelActive,
    isBusinessFieldDisabled: isNameChannelActive,
  };
};

export default useLogisticOperatorsSearchBarLocalState;
